import { Message } from '@/types';

/**
 * Check if welcome back message should be shown
 * Returns true if user has been away for 30+ minutes since last message
 * AND at least 2 hours have passed since the last welcome back message
 */
export const shouldShowWelcomeBack = (
  messages: Message[],
  lastWelcomeBackShown?: number | null
): boolean => {
  if (!messages || messages.length === 0) {
    return false;
  }

  const now = Date.now();
  const THIRTY_MINUTES = 30 * 60 * 1000; // 30 minutes in milliseconds
  const TWO_HOURS = 2 * 60 * 60 * 1000; // 2 hours in milliseconds

  // Get timestamp of last message (use spread to avoid mutating original array)
  const lastMessage = [...messages].sort(
    (a, b) => b.timestamp - a.timestamp
  )[0];
  if (!lastMessage) {
    return false;
  }

  // Check if user was away for 30+ minutes since last message
  const wasAwayLongEnough = now - lastMessage.timestamp > THIRTY_MINUTES;

  // Check if at least 2 hours passed since last welcome back message
  const enoughTimeSinceLastWelcomeBack =
    !lastWelcomeBackShown || now - lastWelcomeBackShown > TWO_HOURS;

  // Also check if welcome back was shown before the last message (user returned again)
  const welcomeBackNotShownForThisReturn =
    !lastWelcomeBackShown || lastWelcomeBackShown < lastMessage.timestamp;

  return (
    wasAwayLongEnough &&
    enoughTimeSinceLastWelcomeBack &&
    welcomeBackNotShownForThisReturn
  );
};

/**
 * Get a welcome back message with variations
 * Cycles through different greeting messages to keep the experience fresh
 */
export const getWelcomeBackMessage = (
  name?: string,
  lastShownIndex: number = 0
): { message: string; nextIndex: number } => {
  const nameGreeting = name ? `, ${name}` : '';

  const variations = [
    `Welcome back${nameGreeting}! 👋 Is there anything else I can help you with?`,
    `Great to see you again${nameGreeting}! 👋 How can I assist you today?`,
    `Hey${nameGreeting}! 👋 You're back! What can I help you with?`,
    `Hello again${nameGreeting}! 👋 Ready to continue where we left off?`,
    `Welcome back${nameGreeting}! 👋 Need help with something else?`,
    `Hi there${nameGreeting}! 👋 What brings you back today?`,
    `Nice to see you return${nameGreeting}! 👋 How can I help?`,
  ];

  // Get next message index (cycle through variations)
  const nextIndex = (lastShownIndex + 1) % variations.length;
  const message = variations[nextIndex];

  return { message, nextIndex };
};
